/** The dashboard band on top of a data page: KPI tiles (with a change and a small line) and two or
 * three small charts - KD's direction of 2026-11-01.
 *
 * **This is the slot, not the charts.** It lays out what it is given: `tiles` (a `TileStrip` of
 * tiles that may carry `delta` and `spark`) over `charts` (two or three chart cards, each drawn by
 * the package that owns the page, from reads that already exist). It draws no chart, computes no
 * figure and has no children of its own beyond those two slots, so the text diet holds: a band holds
 * numbers and pictures, not paragraphs. Hover tooltips and motion are the charts' own (the chart
 * module draws in tokens and respects reduced motion).
 *
 * `TablePage` places it between the header card and the filter line (`band`). A page with only a
 * strip of tiles uses `TablePage`'s `tiles` slot instead.
 *
 * **It honours the Charts toggle** (`ChartsContext`, KD's design review): the tiles are always shown,
 * the charts while the toggle in the filter line is open - which is the default (KD, 2026-11-03: the
 * dashboard is part of the page) - and a reader who wants the table higher collapses them and the
 * choice is remembered. Outside a `TablePage` there is no toggle and the charts are shown.
 */

import { Children, useContext } from "react";
import type { ReactNode } from "react";

import { ChartsContext } from "./charts";

import styles from "./DashboardBand.module.css";

export const CHARTS_MIN = 2;
export const CHARTS_MAX = 3;

export function DashboardBand({
  tiles,
  charts,
  label = "Dashboard",
}: {
  tiles?: ReactNode;
  /** Two or three chart cards. */
  charts?: ReactNode;
  label?: string;
}) {
  const slot = useContext(ChartsContext);
  const showCharts = slot ? slot.open : true;
  const count = Children.count(charts);
  if (import.meta.env.DEV && charts && (count < CHARTS_MIN || count > CHARTS_MAX)) {
    throw new Error(
      `A dashboard band holds ${String(CHARTS_MIN)} to ${String(CHARTS_MAX)} charts; this one has ${String(count)}.`,
    );
  }
  return (
    <section className={styles.band} aria-label={label} data-dashboard-band>
      {tiles}
      {charts && showCharts ? (
        <div className={styles.charts} data-chart-count={count} data-chart>
          {charts}
        </div>
      ) : null}
    </section>
  );
}
